/**
 * TaskSubPanels - collaborators, comments and attachments for an open task.
 *
 * PASS B of the task detail. Each is its own fetch with its own loading, so
 * they are three small components rather than one, and each is LAZY: nothing
 * loads until the task is actually selected, and a suggestion (which has no
 * task id yet) never mounts them at all.
 *
 * Endpoints:
 *   collaborators  `listTaskCollaborators` / `addTaskCollaborator` / `removeTaskCollaborator`
 *   comments       `listTaskComments` / `addTaskComment`
 *   attachments    `listTaskAttachments` / `uploadTaskAttachment` /
 *                  `downloadTaskAttachment` / `removeTaskAttachment`
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiPaperclipLight,
  PiUsersLight,
} from 'react-icons/pi';
import { useAppDispatch } from '../../../../store';
import {
  addTaskCollaborator,
  addTaskComment,
  downloadTaskAttachment,
  listTaskAttachments,
  listTaskComments,
  listTaskCollaborators,
  removeTaskAttachment,
  removeTaskCollaborator,
  updateCollaboratorPrivilege,
  uploadTaskAttachment,
} from '../../../../store/actions/TasksActions';
import Button from '../../../shared/TailwindComponents/Button';
import Textarea from '../../../shared/TailwindComponents/Textarea';
import {
  AttachmentDropzone,
  AttachmentList,
  toAttachments,
  sortAttachments,
  Attachment,
} from '../../../shared/attachments';
import CollaboratorSelector, {
  Collaborator as SelectorCollaborator,
  personInitials,
  personName,
} from '../../../shared/CollaboratorSelector';
import { formatDateTime } from '../../../../utils/functions';
import Spinner from '../../../shared/TailwindComponents/Spinner';

/** Collapsible section header - the three panels share one look. */
const SubSection: React.FC<{
  icon: React.ReactNode;
  title: string;
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, count, action, children }) => (
  <div className="pt-3 border-t border-hairline-soft">
    <div className="flex items-center justify-between gap-2 mb-2">
      <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-fg-muted">
        <span className="[&>svg]:h-3.5 [&>svg]:w-3.5">{icon}</span>
        {title}
        {count !== undefined && count > 0 && (
          <span className="tabular-nums opacity-70">{count}</span>
        )}
      </span>
      {action}
    </div>
    {children}
  </div>
);

const rowsOf = (payload: any, ...keys: string[]): any[] => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return [];
};

/**
 * `personName` is the one name resolver; this alias only exists so the rest
 * of this file reads the way it did. The local copy it replaces derived from
 * `first_name`/`last_name`, which the collaborator rows do not have.
 */
const nameOf = (person: any): string => personName(person);


/* ── Collaborators ─────────────────────────────────────────────── */

export const TaskCollaborators: React.FC<{
  taskId: string;
  canEdit: boolean;
  /** Matter people, merged in so the matter's own team resolves with avatars. */
  candidates: any[];
}> = ({ taskId, canEdit, candidates }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<SelectorCollaborator[]>([]);
  /** The row the server marks `role: 'creator'` - the task's owner. */
  const [ownerUuid, setOwnerUuid] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dispatch(listTaskCollaborators(taskId));
      const server = rowsOf(data, 'collaborators', 'data');
      /*
        Map the server rows onto the selector's own shape. It renders each
        person with `ProfileImage` + name + email, so the avatar fields have
        to survive the mapping - the matter-collaborator shape calls it
        `avatar_url`, the selector wants `profile_picture`.
      */
      setRows(server.map((row: any) => {
        /*
          `collaborator_id` FIRST - it is what this endpoint actually sends,
          and leaving it out of the chain meant every row resolved to an empty
          uuid: no directory match, no name, no avatar, and every row keyed
          the same.
        */
        const uuid = String(
          row.collaborator_id || row.collaborator_uuid || row.user_uuid || row.uuid || ''
        );
        // Fall back to the matter's own record for anything the task row
        // did not enrich (a bare row is often just a uuid + access).
        const known = candidates.find((c: any) =>
          String(c.collaborator_id || c.user_uuid || c.user_id || '') === uuid);
        return {
          collaborator_uuid: uuid,
          user_id: row.user_id ?? known?.user_id,
          // `admin` is a real level here (the creator holds it), so it is
          // carried through rather than defaulted away.
          access: (row.access || 'view') as SelectorCollaborator['access'],
          name: nameOf(row) || nameOf(known) || undefined,
          email: row.email || known?.email || undefined,
          initials: row.initials || known?.initials || personInitials(row) || undefined,
          profile_picture: row.avatar_url || row.profile_picture || known?.avatar_url || null,
        };
      }));
      const creator = server.find((row: any) => row.role === 'creator');
      setOwnerUuid(creator
        ? String(creator.collaborator_id || creator.collaborator_uuid || '') || undefined
        : undefined);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, taskId, candidates]);

  useEffect(() => { load(); }, [load]);

  /**
   * The selector is a staged-list editor; task collaborators are server rows
   * mutated one at a time. So the new list is DIFFED against the old and the
   * difference is issued as adds, removes and privilege changes.
   */
  const sync = async (next: SelectorCollaborator[]) => {
    const before = new Map(rows.map((r) => [String(r.collaborator_uuid), r]));
    const after = new Map(next.map((r) => [String(r.collaborator_uuid), r]));
    setSyncing(true);
    try {
      for (const [uuid, row] of Array.from(after)) {
        const prev = before.get(uuid);
        if (!prev) {
          await dispatch(addTaskCollaborator(taskId, uuid, uuid, row.access));
        } else if (prev.access !== row.access) {
          await dispatch(updateCollaboratorPrivilege(taskId, uuid, row.access));
        }
      }
      for (const uuid of Array.from(before.keys())) {
        if (!after.has(uuid)) await dispatch(removeTaskCollaborator(taskId, uuid));
      }
      await load();
    } finally {
      setSyncing(false);
    }
  };

  return (
    <SubSection
      icon={<PiUsersLight />}
      title={t('Collaborators')}
      count={rows.length}
      action={syncing ? <Spinner size="glyph" className="!h-3.5 !w-3.5" /> : undefined}
    >
      {loading ? (
        <Spinner size="glyph" className="!h-4 !w-4" />
      ) : (
        <CollaboratorSelector
          collaborators={rows}
          onCollaboratorsChange={sync}
          allowedPermissions={['edit', 'view']}
          addPlaceholder={t('Add a person')}
          /* The creator's access is not editable or removable from here, and
             `admin` is not one of the two levels this control offers - so the
             row renders as an Owner badge instead of an empty dropdown. */
          adminUuid={ownerUuid}
          /* No owner/lead here - a task's owner is the matter's, not its own. */
          showOwnerSection={false}
          readOnly={!canEdit}
        />
      )}
    </SubSection>
  );
};

/* ── Comments ──────────────────────────────────────────────────── */

export const TaskComments: React.FC<{ taskId: string; canEdit: boolean }> = ({
  taskId,
  canEdit,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dispatch(listTaskComments(taskId));
      setRows(rowsOf(data, 'comments', 'data'));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, taskId]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await dispatch(addTaskComment(taskId, draft.trim()));
      setDraft('');
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <SubSection icon={<PiUsersLight />} title={t('Comments')} count={rows.length}>
      {loading ? (
        <Spinner size="glyph" className="!h-4 !w-4" />
      ) : (
        <div className="space-y-2">
          {rows.length === 0 && (
            <p className="text-xs text-fg-muted">{t('No comments yet.')}</p>
          )}
          {rows.map((row, index) => (
            <div key={row.id || row.uuid || index} className="rounded-md bg-surface-sunken/70 border border-hairline-soft p-2">
              <p className="text-xs text-fg-body whitespace-pre-wrap">
                {row.comment_text || row.text || row.comment}
              </p>
              <p className="text-[10px] text-fg-muted mt-1">
                {nameOf(row.user || row) || t('Someone')}
                {row.created_at ? ` · ${formatDateTime(row.created_at)}` : ''}
              </p>
            </div>
          ))}

          {canEdit && (
            <div className="space-y-1.5 pt-1">
              <Textarea
                id={`comment-${taskId}`}
                label=""
                placeholder={t('Add a comment…')}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={2}
                className="w-full"
              />
              <div className="flex justify-end">
                <Button variant="outline" size="small" onClick={post} disabled={!draft.trim() || busy}>
                  {busy ? t('Posting...') : t('Comment')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </SubSection>
  );
};

/* ── Attachments ───────────────────────────────────────────────── */

export const TaskAttachments: React.FC<{ taskId: string; canEdit: boolean }> = ({
  taskId,
  canEdit,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dispatch(listTaskAttachments(taskId));
      setRows(sortAttachments(toAttachments(data)));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, taskId]);

  useEffect(() => { load(); }, [load]);

  /**
   * The task endpoint takes ONE file at a time and stores it server-side
   * immediately, so a dropped batch is uploaded file by file and the server
   * list below is re-read - that list, not the dropzone, is the truth.
   */
  const upload = async (staged: File[]) => {
    if (!staged.length) return;
    setBusy(true);
    try {
      for (const file of staged) {
        await dispatch(uploadTaskAttachment(taskId, file));
      }
      await load();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (attachmentId: string) => {
    setBusy(true);
    try {
      await dispatch(removeTaskAttachment(taskId, attachmentId));
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <SubSection
      icon={<PiPaperclipLight />}
      title={t('Attachments')}
      count={rows.length}
      action={busy ? <Spinner size="glyph" className="!h-3.5 !w-3.5" /> : undefined}
    >
      {canEdit && (
        <AttachmentDropzone
          className="mb-2"
          density="pane"
          onUpload={upload}
          disabled={busy}
        />
      )}
      <AttachmentList
        attachments={rows}
        loading={loading}
        density="pane"
        busy={busy}
        skeletonRows={3}
        onOpen={(a) => dispatch(downloadTaskAttachment(taskId, a.id, a.name || t('File')))}
        onDownload={(a) => dispatch(downloadTaskAttachment(taskId, a.id, a.name || t('File')))}
        onRemove={(a) => remove(a.id)}
        canRemove={canEdit}
        emptyTitle={t('No attachments')}
      />
    </SubSection>
  );
};
